import { z } from 'zod';
import { DisputeType, DisputeStatus } from './dispute.enums';
import { EntitlementKind } from '../event';
import { PaginationResponseSchema } from '../common';

export const DisputeResponseSchema = z.object({
  id: z.uuid(),
  transactionId: z.uuid(),
  eventId: z.uuid().optional(),
  bookingId: z.uuid().optional(),
  userId: z.uuid(),
  disputeType: z.enum(DisputeType),
  reason: z.string(),
  amountInDispute: z.number(),
  disputeStatus: z.enum(DisputeStatus),
  resolution: z.string().optional(),
  reportedAt: z.iso.datetime(),
  slaDeadline: z.iso.datetime(),
  resolvedAt: z.iso.datetime().optional(),
  createdAt: z.iso.datetime(),
});

export const DISPUTE_DETAIL_MAX_LENGTH = 500;
export const BOOKING_DISPUTE_DETAIL_MAX_LENGTH = 2000;

/** Viewer entitlement dispute reasons (Prisma `DisputeReason` subset). */
export const DisputeReasonSchema = z.enum([
  'EVENT_DID_NOT_RUN',
  'POOR_QUALITY',
  'ACCESS_ISSUE',
  'OTHER',
]);

/** Crew / vendor booking dispute reasons (Prisma `DisputeReason` subset). */
export const BookingDisputeReasonSchema = z.enum([
  'BOOKING_SERVICE_NOT_DELIVERED',
  'BOOKING_SERVICE_POOR_QUALITY',
  'BOOKING_SCOPE_MISMATCH',
  'BOOKING_OTHER',
]);

/** Full Prisma `DisputeReason` surface for admin reads. */
export const AnyDisputeReasonSchema = z.enum([
  ...DisputeReasonSchema.options,
  ...BookingDisputeReasonSchema.options,
]);

export const DisputeSubjectTypeSchema = z.enum(['ENTITLEMENT', 'BOOKING']);

// Mirrors Prisma `DisputeState` enum.
export const DisputeStateSchema = z.enum(['OPEN', 'RESOLVED_CUSTOMER', 'RESOLVED_HOST', 'EXPIRED']);

// Mirrors Prisma `DisputeOutcome` enum.
export const DisputeOutcomeSchema = z.enum(['REFUNDED', 'DENIED', 'PARTIAL_REFUND']);

const EntitlementKindSchema = z.enum(EntitlementKind);

export const OpenDisputeRequestSchema = z
  .object({
    entitlementKind: EntitlementKindSchema,
    reason: DisputeReasonSchema,
    detail: z.string().trim().min(1).max(DISPUTE_DETAIL_MAX_LENGTH).optional(),
  })
  .strict();

/** `POST /marketplace/bookings/:id/dispute` */
export const RaiseBookingDisputeRequestSchema = z
  .object({
    reason: BookingDisputeReasonSchema,
    detail: z.string().trim().min(1).max(BOOKING_DISPUTE_DETAIL_MAX_LENGTH).optional(),
  })
  .strict();

export const RaiseBookingDisputeResponseSchema = z
  .object({
    bookingId: z.uuid(),
    dispute: z.object({
      id: z.uuid(),
      state: DisputeStateSchema,
      raisedBy: z.enum(['HOST', 'VENDOR']),
    }),
  })
  .strict();

/** `GET /marketplace/bookings/:id/dispute` — queryable row linked to the booking. */
export const GetBookingDisputeResponseSchema = z
  .object({
    id: z.uuid(),
    bookingId: z.uuid(),
    subjectType: z.literal('BOOKING'),
    userId: z.uuid(),
    reason: BookingDisputeReasonSchema,
    detail: z.string().nullable(),
    state: DisputeStateSchema,
    raisedBy: z.enum(['HOST', 'VENDOR']),
    resolvedAt: z.iso.datetime().nullable(),
    resolutionOutcome: DisputeOutcomeSchema.nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .strict();

export const AdminDisputeResponseSchema = z
  .object({
    id: z.uuid(),
    subjectType: DisputeSubjectTypeSchema,
    subjectId: z.string().min(1),
    entitlementId: z.string().nullable(),
    eventId: z.uuid().nullable(),
    bookingId: z.uuid().nullable(),
    userId: z.uuid(),
    reason: AnyDisputeReasonSchema,
    detail: z.string().nullable(),
    state: DisputeStateSchema,
    resolvedAt: z.iso.datetime().nullable(),
    resolutionOutcome: DisputeOutcomeSchema.nullable(),
    correlationId: z.string(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
  })
  .strict();

export const MyDisputesQuerySchema = z
  .object({
    state: DisputeStateSchema.optional(),
    pageNumber: z.coerce.number().int().min(1).optional(),
    pageSize: z.coerce.number().int().min(1).max(100).optional(),
  })
  .strict();

export const AdminDisputesQuerySchema = z
  .object({
    state: DisputeStateSchema.optional(),
    eventId: z.uuid().optional(),
    bookingId: z.uuid().optional(),
    subjectType: DisputeSubjectTypeSchema.optional(),
    pageNumber: z.coerce.number().int().min(1).optional(),
    pageSize: z.coerce.number().int().min(1).max(100).optional(),
  })
  .strict();

export const ResolveDisputeRequestSchema = z
  .object({
    outcome: DisputeOutcomeSchema,
    refundId: z.string().min(1).max(100).optional(),
    resolutionNote: z.string().trim().max(500).optional(),
  })
  .strict();

export const PagedDisputesResponseSchema = z
  .object({
    items: z.array(AdminDisputeResponseSchema),
    ...PaginationResponseSchema.shape,
  })
  .strict();

export const AdminRevokeViewerPlaybackSchema = z.object({
  viewerId: z.uuid(),
  reason: z.string().min(1).max(500).optional(),
});

export const AdminRevokedViewersResponseSchema = z.object({
  eventId: z.uuid(),
  viewerIds: z.array(z.uuid()),
});
